import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { previewImportContacts, importContacts } from "@/lib/api";
import {
  Loader2,
  Upload,
  FileText,
  Check,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "first_name", label: "First name" },
  { key: "last_name", label: "Last name" },
  { key: "full_name", label: "Full name" },
  { key: "email", label: "Email" },
  { key: "sms", label: "SMS / mobile" },
  { key: "landline", label: "Landline" },
  { key: "company", label: "Company" },
  { key: "address", label: "Address" },
  { key: "address2", label: "Address 2" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "zip", label: "Zip" },
];

const MAX_PER_SYNC = 50;

const STAGE = { paste: 0, review: 1, done: 2 };

export default function ImportContactsDialog({
  open,
  onOpenChange,
  userRecordId,
  onCompleted,
}) {
  const [stage, setStage] = useState(STAGE.paste);
  const [csvText, setCsvText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  // Reset on close so reopening starts fresh
  const handleOpenChange = (next) => {
    if (!next) {
      setStage(STAGE.paste);
      setCsvText("");
      setPreview(null);
      setMapping({});
      setResult(null);
    }
    onOpenChange(next);
  };

  async function handleAnalyze() {
    if (!csvText.trim()) {
      toast.error("Paste or upload some contacts first");
      return;
    }
    setParsing(true);
    try {
      const res = await previewImportContacts(csvText);
      if (!res.rows?.length) {
        toast.error("Could not detect any rows. Check the file?");
        return;
      }
      setPreview(res);
      setMapping(res.mapping || {});
      setStage(STAGE.review);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Parse failed");
    } finally {
      setParsing(false);
    }
  }

  async function handleImport() {
    setImporting(true);
    try {
      const res = await importContacts({
        userRecordId,
        csvText,
        mapping,
      });
      setResult(res);
      setStage(STAGE.done);
      if (res.added + res.linked > 0) {
        toast.success(
          `${res.added} new · ${res.linked} linked · ${res.skipped} skipped`
        );
      } else {
        toast.error(`No contacts imported — ${res.skipped} skipped`);
      }
      onCompleted?.(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Import failed");
    } finally {
      setImporting(false);
    }
  }

  function setField(field, idx) {
    setMapping((m) => {
      const next = { ...m };
      // Unset any other field currently using this column
      Object.keys(next).forEach((k) => {
        if (next[k] === idx) delete next[k];
      });
      if (idx === -1) {
        delete next[field];
      } else {
        next[field] = idx;
      }
      return next;
    });
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto"
        data-testid="import-contacts-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Upload size={18} className="text-violet-600" />
            Import contacts
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Drop a CSV, TSV, or paste rows from a spreadsheet. We&apos;ll auto-detect
            columns and forgive messy separators.
          </DialogDescription>
        </DialogHeader>

        {stage === STAGE.paste && (
          <PasteStage
            csvText={csvText}
            setCsvText={setCsvText}
            fileRef={fileRef}
          />
        )}

        {stage === STAGE.review && preview && (
          <ReviewStage
            preview={preview}
            mapping={mapping}
            setField={setField}
          />
        )}

        {stage === STAGE.done && result && <DoneStage result={result} />}

        <DialogFooter className="flex sm:justify-between gap-2 pt-2">
          {stage === STAGE.paste && (
            <>
              <Button
                variant="ghost"
                onClick={() => handleOpenChange(false)}
                className="rounded-xl"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAnalyze}
                disabled={parsing}
                className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="import-analyze-btn"
              >
                {parsing ? (
                  <Loader2 className="animate-spin mr-2" size={14} />
                ) : (
                  <FileText size={14} className="mr-2" />
                )}
                Detect columns
              </Button>
            </>
          )}
          {stage === STAGE.review && (
            <>
              <Button
                variant="ghost"
                onClick={() => setStage(STAGE.paste)}
                disabled={importing}
                className="rounded-xl"
              >
                Back
              </Button>
              <Button
                onClick={handleImport}
                disabled={importing}
                className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="import-confirm-btn"
              >
                {importing ? (
                  <Loader2 className="animate-spin mr-2" size={14} />
                ) : (
                  <Check size={14} className="mr-2" />
                )}
                Import {Math.min(preview.total, MAX_PER_SYNC)} contact{Math.min(preview.total, MAX_PER_SYNC) === 1 ? "" : "s"}
              </Button>
            </>
          )}
          {stage === STAGE.done && (
            <>
              <Button
                variant="ghost"
                onClick={() => setStage(STAGE.paste)}
                className="rounded-xl"
                data-testid="import-again-btn"
              >
                <RefreshCw size={14} className="mr-2" />
                Import more
              </Button>
              <Button
                onClick={() => handleOpenChange(false)}
                className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="import-close-btn"
              >
                Done
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PasteStage({ csvText, setCsvText, fileRef }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          className="rounded-xl"
          onClick={() => fileRef.current?.click()}
          data-testid="import-choose-file"
        >
          <FileText size={14} className="mr-1.5" />
          Choose file
        </Button>
        <span className="text-xs text-zinc-500">
          .csv · .tsv · or paste below
        </span>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.tsv,.txt,text/csv,text/plain"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = () => setCsvText(String(reader.result || ""));
            reader.readAsText(file);
          }}
        />
      </div>
      <Textarea
        value={csvText}
        onChange={(e) => setCsvText(e.target.value)}
        rows={9}
        placeholder={
          "Jane Doe, jane@example.com, 555-123-4567\n" +
          "John Smith, john@example.com, 555-222-3333\n\n" +
          "or with headers:\nFirst Name,Last Name,Email,Mobile,Home Tel"
        }
        className="font-mono text-xs rounded-xl"
        data-testid="import-csv-textarea"
      />
      <p className="text-[11px] text-zinc-500">
        We accept commas, semicolons, tabs, or pipes. Quoted fields with commas
        inside (like &ldquo;Doe, Jr.&rdquo;) are fine.
      </p>
    </div>
  );
}

function ReviewStage({ preview, mapping, setField }) {
  const colChoices = preview.headers.map((h, i) => ({
    idx: i,
    label: h || `Column ${i + 1}`,
  }));

  return (
    <div className="space-y-4">
      {preview.total > MAX_PER_SYNC && (
        <div
          className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
          data-testid="import-limit-warning"
        >
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            You pasted {preview.total} rows. Only the first {MAX_PER_SYNC} will be
            imported this time — paste the rest in another batch.
          </span>
        </div>
      )}
      {/* Mapping picker */}
      <div className="rounded-xl border border-zinc-200 bg-zinc-50/60 p-3">
        <div className="text-xs font-semibold text-zinc-700 mb-2">
          Column mapping
        </div>
        <div className="grid grid-cols-2 gap-2">
          {FIELDS.map((f) => (
            <div key={f.key} className="space-y-1">
              <Label className="text-[11px] text-zinc-600">{f.label}</Label>
              <Select
                value={
                  mapping[f.key] !== undefined ? String(mapping[f.key]) : "-1"
                }
                onValueChange={(v) => setField(f.key, Number(v))}
              >
                <SelectTrigger
                  className="h-9 rounded-lg text-xs"
                  data-testid={`import-map-${f.key}`}
                >
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="-1">— skip —</SelectItem>
                  {colChoices.map((c) => (
                    <SelectItem key={c.idx} value={String(c.idx)}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </div>

      {/* Preview table */}
      <div className="rounded-xl border border-zinc-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="import-preview-table">
            <thead className="bg-zinc-100 text-zinc-700">
              <tr>
                {preview.headers.map((h, i) => (
                  <th key={i} className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                    {h || `Col ${i + 1}`}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {preview.rows.map((r, ri) => (
                <tr key={ri}>
                  {r.map((c, ci) => (
                    <td
                      key={ci}
                      className="px-3 py-1.5 whitespace-nowrap text-zinc-700 max-w-[140px] truncate"
                      title={c}
                    >
                      {c}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-zinc-50 px-3 py-1.5 text-[11px] text-zinc-500 border-t border-zinc-200">
          Showing first {preview.rows.length} of {preview.total} rows
        </div>
      </div>
    </div>
  );
}

function DoneStage({ result }) {
  const totalAdded = (result.added || 0) + (result.linked || 0);
  return (
    <div className="space-y-3" data-testid="import-result">
      <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 p-4 text-center">
        <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mb-2">
          <Check size={22} />
        </div>
        <div className="text-lg font-semibold text-zinc-900">
          {totalAdded} contact{totalAdded === 1 ? "" : "s"} imported
        </div>
        <div className="text-xs text-zinc-500 mt-1">
          {result.added} new · {result.linked} linked · {result.skipped} skipped
        </div>
      </div>

      {result.errors?.length > 0 && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-800 mb-1.5">
            <AlertTriangle size={13} />
            {result.errors.length} row{result.errors.length === 1 ? "" : "s"} had issues
          </div>
          <ul className="text-[11px] text-amber-800 space-y-0.5 max-h-32 overflow-y-auto">
            {result.errors.map((e, i) => (
              <li key={i}>
                Row {e.row}: {e.error}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
